import {
  Box, Chart, Picker, Text,
} from '@usealloy/component-library';
import { useMemo } from 'react';
import {
  CellDiv,
} from '../MatrixModel/styles';
import { AxisHeaderTextInput } from 'flow/editor/components/NodeEditorDrawer/MatrixModel/AxisHeaderInput/components/AxisHeaderTextInput';
import {
  HighRangeOption,
  LowRangeOption,
} from 'flow/service/actions';
import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';
import { calculateColorRanges, getFormattedRangeLabel } from './helpers';
import { StyledColorPickerCell, StyledReadOnlyTable } from './styles';

interface ReadOnlyValueRangeConfigProps {
  outputValueRange: OutputValueRange[];
  outputValueRangeOptions: ValueRangeConfigOptions;
}

export const ReadOnlyValueRangeConfig = ({
  outputValueRange,
  outputValueRangeOptions,
}: ReadOnlyValueRangeConfigProps) => {
  const isSingleValue = outputValueRange.length === 1
    && outputValueRangeOptions.lowRange !== LowRangeOption.unbounded
    && outputValueRangeOptions.highRange !== HighRangeOption.unbounded;

  const tableRows = useMemo(() => outputValueRange.map((range, index: number) => {
    const {
      label, color, minValue, maxValue,
    } = range;

    const isFirstRow = index === 0;
    const isLastRow = index === outputValueRange.length - 1;
    const isFirstUnbounded = isFirstRow
      && outputValueRangeOptions.lowRange === LowRangeOption.unbounded;
    const isLastUnbounded = isLastRow
      && outputValueRangeOptions.highRange === HighRangeOption.unbounded;

    const { minLabel, maxLabel } = getFormattedRangeLabel({
      minValue,
      maxValue,
      isFirstUnbounded,
      isLastUnbounded,
    });

    const rangeValues = isSingleValue
      ? String(minLabel)
      : `${minLabel} - ${maxLabel}`;

    return {
      rangeValues,
      label,
      color,
    };
  }), [outputValueRange, outputValueRangeOptions, isSingleValue]);

  const { colorRanges } = useMemo(
    () => calculateColorRanges({
      outputValueRange,
      outputValueRangeOptions,
      includePendingRanges: false,
    }),
    [outputValueRange, outputValueRangeOptions],
  );

  if (outputValueRange.length === 0) {
    return null;
  }

  return (
    <Box
      direction="column"
      gap={0}
      justifyContent="flex-start"
      style={{ marginLeft: 'calc(-1 * var(--cl-space-3))' }}
    >
      <Box justifyContent="flex-start" style={{ paddingLeft: 'var(--cl-space-3)' }}>
        <Text>Configure Value Ranges</Text>
      </Box>
      {colorRanges.length > 0 && (
        <div
          style={{
            minWidth: '100%',
            marginTop: 'calc(-1 * var(--cl-space-7))',
            zIndex: -1,
          }}
        >
          <Chart.Bullet
            colorRanges={colorRanges}
          />
        </div>
      )}
      <Box direction="column" alignItems="flex-start" justifyContent="flex-start">
        <Box style={{
          padding: '0 var(--cl-space-3)', width: '100%',
        }}
        >
          <StyledReadOnlyTable is2Dimensional={false}>
            <table>
              <tbody>
                {tableRows.map((row, index) => (
                  <tr key={row.rangeValues}>
                    <td>
                      <AxisHeaderTextInput
                        autoComplete="off"
                        type="text"
                        placeholder=""
                        value={row.rangeValues}
                        id={`value-range-axis-header-${index}`}
                        onChange={() => {}}
                        onBlur={() => {}}
                        onKeyDown={() => {}}
                        readOnly
                      />
                    </td>
                    <td>
                      <Box data-dd-action-name={`value-range-config-cell-${row.label}`}>
                        <CellDiv>{row.label}</CellDiv>
                      </Box>
                    </td>
                    <StyledColorPickerCell>
                      <Box data-dd-action-name={`value-range-config-cell-${row.label}`}>
                        <Picker
                          style={{ border: 'none' }}
                          variant="color"
                          value={row.color}
                          disabled
                        />
                      </Box>
                    </StyledColorPickerCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledReadOnlyTable>
        </Box>
      </Box>
    </Box>
  );
};

